import { action, computed, observable, reaction } from 'mobx'
import type { ActivationLifecycle } from './activation'

/**
 * Options for the view model list.
 */
export interface ViewModelListOptions<TModel, TViewModel> {
  /**
   * Reactive getter for the models.
   */
  models: () => readonly TModel[]
  /**
   * Callback for creating a new view model for a model.
   * @param model
   */
  create: (model: TModel) => TViewModel
  /**
   * Callback for activating a view model.
   * @param viewModel
   */
  activate?: (viewModel: TViewModel) => void
  /**
   * Callback for deactivating a view model.
   * @param viewModel
   */
  deactivate?: (viewModel: TViewModel) => void
}

/**
 * Reactively maintains a list of view models that are based on observable models.
 */
export class ViewModelList<TModel, TViewModel> implements ActivationLifecycle {
  /**
   * Maintains a map from a model and a view model.
   *
   * @private
   */
  @observable private accessor _modelToViewModelMap = new Map<
    TModel,
    TViewModel
  >()

  /**
   * Maintains a map from a view model to a model.
   *
   * @private
   */
  @observable private accessor _viewModelToModelMap = new Map<
    TViewModel,
    TModel
  >()

  /**
   * Used to dispose the sync pipeline.
   *
   * @private
   */
  private _disposeSync: (() => void) | null = null

  /**
   * The underlying items. We use `.ref` because we will be replacing the array when we sync.
   *
   * @private
   */
  @observable.ref private accessor _items: TViewModel[] = []

  /**
   * Constructor.
   *
   * @param opts
   */
  constructor(private opts: ViewModelListOptions<TModel, TViewModel>) {}

  /**
   * The items.
   */
  @computed
  get items() {
    return this._items
  }

  /**
   * Whether the list contains the given model instance.
   *
   * @param model
   */
  contains(model: TModel) {
    return this._modelToViewModelMap.has(model)
  }

  /**
   * Gets the view model for the specified model, if any.
   *
   * @param model
   */
  get(model: TModel) {
    return this._modelToViewModelMap.get(model)
  }

  /**
   * Activates the list and syncs the models.
   */
  @action.bound
  activate() {
    this._disposeSync?.()
    this._disposeSync = reaction(this.opts.models, this._syncItems, {
      fireImmediately: true,
    })
  }

  /**
   * Deactivates the list and stops syncing the models. Also
   * deactivates the view models.
   */
  @action.bound
  deactivate() {
    this._disposeSync?.()
    if (this.opts.deactivate) {
      for (const item of this._items) {
        this.opts.deactivate?.(item)
      }
    }

    this._items = []
    this._modelToViewModelMap.clear()
    this._viewModelToModelMap.clear()
  }

  /**
   * Syncs the items.
   *
   * @param models
   * @private
   */
  @action.bound
  private _syncItems(models: readonly TModel[]) {
    // We know the size of the new collection
    const len = models.length
    const modelsSet = new Set(models)
    const newItems = new Array<TViewModel>(len)

    // Add new and existing items
    for (let i = 0; i < len; i++) {
      const model = models[i]
      const existing = this._modelToViewModelMap.get(model)
      if (existing) {
        newItems[i] = existing
        continue
      }

      newItems[i] = this._createAndActivate(model)
    }

    // Deactivate items that are no longer present
    for (const oldViewModel of this._items) {
      // We know the model will be present
      const oldModel = this._viewModelToModelMap.get(oldViewModel)!
      if (modelsSet.has(oldModel)) {
        continue
      }

      // The view model can be disposed of
      this.opts.deactivate?.(oldViewModel)
      this._modelToViewModelMap.delete(oldModel)
      this._viewModelToModelMap.delete(oldViewModel)
    }

    this._items = newItems
  }

  /**
   * Creates and activates a view model.
   *
   * @param model
   * @private
   */
  private _createAndActivate(model: TModel): TViewModel {
    const viewModel = this.opts.create(model)
    this._modelToViewModelMap.set(model, viewModel)
    this._viewModelToModelMap.set(viewModel, model)
    this.opts.activate?.(viewModel)
    return viewModel
  }
}
